import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import { TeamMemberStatusDTO } from '@/modules/common/types/team-member-status-dto';

import { PlannerUserStateChangeResponse } from '../../types/dto';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

interface PreparedStateChange {
  change: PlannerUserStateChangeResponse;
  startTime: number;
  endTime: number | null;
}

/**
 * Prepares state changes for binary search by converting dates to timestamps.
 *
 * Note: WeakMap caching was removed as it caused memory leaks. Arrays are recreated on every
 * render/data fetch, causing infinite cache growth. This function is called frequently but
 * is fast enough without caching.
 */
const getPreparedStateChanges = (
  stateChanges: PlannerUserStateChangeResponse[]
): PreparedStateChange[] => {
  return stateChanges
    .map(change => ({
      change,
      startTime: dayjs(change.effectiveStartDate).startOf('day').valueOf(),
      endTime: change.effectiveEndDate
        ? dayjs(change.effectiveEndDate).startOf('day').valueOf()
        : null,
    }))
    .sort((a, b) => a.startTime - b.startTime);
};

const findLastStartedStateIndex = (prepared: PreparedStateChange[], targetTime: number): number => {
  let left = 0;
  let right = prepared.length - 1;
  let result = -1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);

    if (prepared[mid].startTime <= targetTime) {
      result = mid;
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  return result;
};

/**
 * Finds the applicable state change for a given target date.
 *
 * Logic:
 * 1. Find state where effectiveStartDate <= targetDate AND
 *    (effectiveEndDate === null OR effectiveEndDate >= targetDate)
 * 2. If no exact match, find the most recent state that started before the target date
 * 3. Return null if no state changes exist
 *
 * @param stateChanges - Array of state changes from API
 * @param targetDate - The date to look up (dayjs object)
 * @returns The applicable state change or null
 */
export const getStateChangeForDate = (
  stateChanges: PlannerUserStateChangeResponse[] | undefined,
  targetDate: dayjs.Dayjs | null
): PlannerUserStateChangeResponse | null => {
  if (!stateChanges || stateChanges.length === 0 || !targetDate) {
    return null;
  }

  const targetTime = targetDate.startOf('day').valueOf();
  const prepared = getPreparedStateChanges(stateChanges);
  const latestStartedIndex = findLastStartedStateIndex(prepared, targetTime);

  if (latestStartedIndex < 0) {
    return null;
  }

  // Scan backwards from the latest started state to find an active interval match.
  for (let index = latestStartedIndex; index >= 0; index--) {
    const candidate = prepared[index];
    if (candidate.endTime === null || targetTime <= candidate.endTime) {
      return candidate.change;
    }
  }

  // Fallback: use the most recently started state before the target date.
  return prepared[latestStartedIndex]?.change ?? null;
};

/**
 * Checks if a specific date is unavailable for a user (not yet joined or archived).
 *
 * @param stateChanges - Array of state changes from API
 * @param userStartDate - The date the user joined (first state change with no changed fields)
 * @param targetDate - The date to check
 * @returns true if the date is unavailable (user hasn't joined yet or is archived)
 */
export const isDateUnavailable = (
  stateChanges: PlannerUserStateChangeResponse[] | undefined,
  userStartDate: dayjs.Dayjs | undefined,
  targetDate: dayjs.Dayjs
): boolean => {
  // User hasn't joined yet on this date
  if (userStartDate && targetDate.isBefore(userStartDate, 'day')) {
    return true;
  }

  // Check if user is archived on this date
  const stateChange = getStateChangeForDate(stateChanges, targetDate);
  if (stateChange?.displayStatus === TeamMemberStatusDTO.ARCHIVED) {
    return true;
  }

  return false;
};

/**
 * Checks if any date in a range is unavailable for a user.
 *
 * @param stateChanges - Array of state changes from API
 * @param userStartDate - The date the user joined
 * @param rangeStart - Start of the date range to check
 * @param rangeEnd - End of the date range to check
 * @returns true if any date in the range is unavailable
 */
export const isDateRangeUnavailable = (
  stateChanges: PlannerUserStateChangeResponse[] | undefined,
  userStartDate: dayjs.Dayjs | undefined,
  rangeStart: dayjs.Dayjs,
  rangeEnd: dayjs.Dayjs
): boolean => {
  let current = rangeStart.startOf('day');
  const end = rangeEnd.startOf('day');

  while (current.isSameOrBefore(end, 'day')) {
    if (isDateUnavailable(stateChanges, userStartDate, current)) {
      return true;
    }
    current = current.add(1, 'day');
  }

  return false;
};
